import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { CheckCircle2, Send, Clock, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { multiApplyAPI, upsellAPI } from '../../lib/api';

const VIEW_KEY = 'rn_upsell_views';
const DISMISS_KEY = 'rn_upsell_dismissed';
const THRESHOLD = 3;
const HIDDEN_PATHS = ['/multi-apply', '/checkout', '/payment', '/thank-you', '/admin', '/super-admin', '/staff', '/login'];
const INACTIVE = ['completed', 'done', 'cancelled'];
let checking = false;

const viewCount = () => JSON.parse(sessionStorage.getItem(VIEW_KEY) || '[]').length;

// Call whenever a user views a job or agency (unique per item)
export const recordView = (id) => {
    const seen = JSON.parse(sessionStorage.getItem(VIEW_KEY) || '[]');
    if (seen.includes(id)) return;
    sessionStorage.setItem(VIEW_KEY, JSON.stringify([...seen, id]));
    window.dispatchEvent(new Event('rn:view'));
};

const BENEFITS = [
    { icon: Send, text: 'Our team applies to up to 250 jobs a month for you' },
    { icon: CheckCircle2, text: 'Tailored applications written by real people' },
    { icon: Clock, text: 'Save 20+ hours a week of form filling' },
    { icon: Users, text: 'Track every application from your dashboard' },
];

export const MultiApplyUpsell = () => {
    const { user, isAuthenticated } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const check = async () => {
            if (!isAuthenticated || !user || user.role !== 'user') return;
            if (sessionStorage.getItem(DISMISS_KEY)) return;
            if (HIDDEN_PATHS.some(p => location.pathname.startsWith(p))) return;
            if (viewCount() < THRESHOLD || checking) return;
            checking = true;
            try {
                const { data } = await multiApplyAPI.getOrders();
                const active = (data.orders || []).some(o => o.payment_status === 'paid' && !INACTIVE.includes(o.status));
                if (active) return sessionStorage.setItem(DISMISS_KEY, '1');
            } catch { return; } finally { checking = false; }
            if (sessionStorage.getItem(DISMISS_KEY)) return;
            sessionStorage.setItem(DISMISS_KEY, '1');
            upsellAPI.track('shown').catch(() => {});
            setOpen(true);
        };
        check();
        window.addEventListener('rn:view', check);
        return () => window.removeEventListener('rn:view', check);
    }, [isAuthenticated, user, location.pathname]);

    const close = () => { upsellAPI.track('dismissed').catch(() => {}); setOpen(false); };
    const go = () => { upsellAPI.track('clicked').catch(() => {}); setOpen(false); navigate('/multi-apply'); };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o && open) close(); }}>
            <DialogContent className="max-w-md p-0 overflow-hidden" data-testid="multi-apply-upsell">
                <div className="bg-gradient-to-br from-primary-700 to-violet-700 px-6 pt-7 pb-6 text-white">
                    <p className="text-xs font-semibold tracking-widest text-amber-300 mb-2">TIRED OF APPLYING?</p>
                    <DialogTitle className="text-2xl font-bold text-white">Let us apply for you</DialogTitle>
                    <DialogDescription className="text-primary-100 mt-2">
                        You've been browsing jobs. With Multi-Apply, our team sends the applications while you prepare for interviews.
                    </DialogDescription>
                </div>
                <div className="px-6 py-5 space-y-3">
                    {BENEFITS.map(({ icon: Icon, text }) => (
                        <div key={text} className="flex items-center gap-3 text-sm text-neutral-700">
                            <Icon className="w-4 h-4 text-emerald-600 shrink-0" />
                            {text}
                        </div>
                    ))}
                    <p className="text-sm text-neutral-500 pt-1">Plans from <span className="font-semibold text-neutral-900">£36/month</span>. Cancel anytime.</p>
                </div>
                <div className="px-6 pb-6 flex gap-3">
                    <Button variant="outline" className="flex-1" onClick={close} data-testid="multi-apply-upsell-dismiss">Not now</Button>
                    <Button className="flex-1 bg-primary-600 hover:bg-primary-700" onClick={go} data-testid="multi-apply-upsell-cta">See Multi-Apply plans</Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};
